import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Menu, X } from 'lucide-react';
import { waLink, DEFAULT_WA_MESSAGE, EMAIL, WHATSAPP_DISPLAY } from '../data/site';

const NAV = [
  { label: 'Serviços', href: '#servicos' },
  { label: 'Simulador', href: '#simulador' },
  { label: 'Diferenciais', href: '#diferenciais' },
  { label: 'Cobertura', href: '#cobertura' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contato', href: '#contato' },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -110, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  }
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div className="hidden sm:block bg-[#0B0C10]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-9 flex items-center justify-between text-[11px] text-white/70">
          <a
            data-testid="topbar-email"
            href={`mailto:${EMAIL}`}
            className="hover:text-white transition-colors"
          >
            {EMAIL}
          </a>
          <a
            data-testid="topbar-phone"
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <MessageCircle size={12} />
            Fale conosco: {WHATSAPP_DISPLAY}
          </a>
        </div>
      </div>

      <div
        className={`bg-blue-600 transition-shadow duration-500 ${
          scrolled ? 'shadow-[0_12px_35px_rgba(37,99,235,0.35)]' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between gap-4">
          <button
            data-testid="nav-logo"
            onClick={() => go('#topo')}
            className="flex flex-col items-start leading-none text-left"
          >
            <span className="font-display font-extrabold text-lg tracking-tight text-white">
              DAVY
            </span>
            <span className="text-[10px] font-mono tracking-[0.3em] text-blue-200 uppercase">
              Despachante
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => (
              <button
                key={item.href}
                data-testid={`nav-link-${item.href.slice(1)}`}
                onClick={() => go(item.href)}
                className="text-sm text-white/85 hover:text-white underline-offset-4 hover:underline transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-simulator-button"
              onClick={() => go('#simulador')}
              className="hidden md:inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors duration-300"
            >
              Consultar débitos
            </button>
            <a
              data-testid="nav-whatsapp-button"
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-white hover:bg-blue-50 text-blue-600 text-sm font-bold rounded-full px-5 py-2.5 transition-colors duration-300"
            >
              <MessageCircle size={16} />
              Fale conosco
            </a>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200 shadow-xl"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  data-testid={`nav-mobile-link-${item.href.slice(1)}`}
                  onClick={() => go(item.href)}
                  className="text-left text-slate-700 hover:text-blue-600 font-medium transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-whatsapp-button"
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold rounded-full px-5 py-3"
              >
                <MessageCircle size={16} />
                Falar no WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
